/**
 * Caps on error events, so one render or poll loop costs a few events
 * instead of the month's quota (free tier: 5,000 errors). The overall cap is
 * a rolling window rather than a page-load total because staff keep the SPA
 * open all day.
 */
const MAX_EVENTS_PER_ISSUE = 3
const MAX_EVENTS_PER_WINDOW = 25
const WINDOW_MS = 10 * 60 * 1000

/** Where an exception was thrown: its innermost in-app frame, else its innermost frame. */
function throwSite(exception) {
  const frames = exception.stacktrace?.frames ?? []
  const frame = frames.filter((candidate) => candidate.in_app).at(-1) ?? frames.at(-1)
  return frame ? ` @ ${frame.filename}:${frame.function}:${frame.lineno}` : ''
}

/**
 * What makes two error events the same error, for counting repeats: the
 * last exception's `type: value` and where it was thrown, so two bugs that
 * share a message stay separate. Message events key on their text.
 */
function issueKey(event) {
  const exception = event.exception?.values?.at(-1)
  if (exception) return `${exception.type}: ${exception.value}${throwSite(exception)}`
  return String(event.message ?? '')
}

/**
 * A `beforeSend` that drops an error once it has been sent
 * `MAX_EVENTS_PER_ISSUE` times, and every error while
 * `MAX_EVENTS_PER_WINDOW` have gone out in the last `WINDOW_MS`. Outside a
 * burst that fills the window, the first occurrences of every distinct error
 * go out; once the window has room again, so do errors not yet seen.
 *
 * The SDK calls `beforeSend` for error events only (see README, "Enrichment
 * goes in an event processor"); anything else that arrives, feedback
 * included, is passed through untouched and never counted.
 *
 * @returns {(event: object) => object|null}
 */
export function createEventBudget() {
  const sentByIssue = new Map()
  let recentSendTimes = []

  return (event) => {
    if (event.type !== undefined) return event

    const now = Date.now()
    recentSendTimes = recentSendTimes.filter((sentAt) => now - sentAt < WINDOW_MS)
    const key = issueKey(event)
    const sentForIssue = sentByIssue.get(key) ?? 0
    if (sentForIssue >= MAX_EVENTS_PER_ISSUE || recentSendTimes.length >= MAX_EVENTS_PER_WINDOW) return null

    sentByIssue.set(key, sentForIssue + 1)
    recentSendTimes = [...recentSendTimes, now]
    return event
  }
}
